<template>
  <div class="organizeAssetTable"
    v-loading="assetsLoading"
  >
    <neb-table v-if="tableRows.length"
      :columns="tableColumns"
      :rows="tableRows"
      :lockColumns="[ 'title' ]"
    >
      <template v-slot:cell--title="{ cellValue, rowIdx }">
        <div class="organizeAssetTable-title">
          <neb-checkbox :value="assetIsSelected(rowIdx)"
            @click.native="e => onCheckboxClick(e, rowIdx)"
          ></neb-checkbox>
          <router-link :to="detailRoute(rowIdx)"
            target="_blank"
            rel="noopener noreferrer"
            :title="cellValue"
          >
            <span>
              {{ truncate(cellValue, 44) }}
            </span>
          </router-link>
        </div>
      </template>

      <template v-slot:cell--keywords="{ cellValue }">
        <value-list-cell :values="cellValue"></value-list-cell>
      </template>

      <template v-slot:cell--topics="{ cellValue }">
        <value-list-cell :values="cellValue"></value-list-cell>
      </template>

      <template v-for="key in propertySlotNames"
        v-slot:[key]="{ cellValue }"
      >
        <value-list-cell :values="cellValue"
          :key="key"
        ></value-list-cell>
      </template>

      <template v-slot:cell--createdDate="{ cellValue }">
        <span>{{ cellValue }}</span>
      </template>

      <template v-slot:cell--sourceDomain="{ cellValue }">
        <span>{{ cellValue }}</span>
      </template>

      <template v-slot:cell--collections="{ cellValue }">
        <value-list-cell :values="cellValue"></value-list-cell>
      </template>

      <template v-slot:cell--wordCount="{ cellValue }">
        <span>{{ cellValue }}</span>
      </template>
    </neb-table>

    <div class="organizeAssetTable-pagination"
      v-if="tableRows.length"
    >
      <neb-paginator :currentPage="page"
        :pageSize="12"
        :totalCount="totalAssetCount"
        @change="onPageChange"
      ></neb-paginator>
    </div>

    <div class="organizeAssetTable-noAssets"
      v-show="tableRows.length < 1"
    >
      <div class="organizeAssetTable-noAssets-logo">
        <p>Sorry! No results were found.</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  isEmpty,
  has,
  uniqBy,
  includes,
  without,
  range,
  uniq
} from 'lodash'
import moment from 'moment'
import ValueListCell from './ValueListCell'

const assetToRow = asset => {
  const row = {
    title: asset.title,
    topics: uniq(asset.topics),
    keywords: uniq(asset.keywords),
    createdDate: moment(asset.created_at).format('YYYY/MM/DD'),
    sourceDomain: new URL(asset.url).hostname,
    collections: uniq(asset.collection_names),
    wordCount: asset.word_count.toLocaleString()
  }

  if (!isEmpty(asset.properties)) {
    asset.properties
      .forEach(prop => {
        const [ name, value ] = prop.split('__')

        if (!has(row, name)) {
          row[name] = []
        }

        row[name] = row[name].concat([ value ])
      })
  }

  return row
}

export default {
  name: 'OrganizeAssetTable',

  components: {
    ValueListCell
  },

  props: {
    assets: {
      type: Array,
      default: () => []
    },
    assetsLoading: {
      type: Boolean,
      default: false
    },
    selectedAssetIndexes: {
      type: Array,
      default: () => []
    },
    page: {
      type: Number,
      default: 1
    },
    totalAssetCount: {
      type: Number,
      default: 0
    }
  },

  computed: {
    tableColumns () {
      let columns = [
        { label: 'Content Title', key: 'title' },
        { label: 'Date Created', key: 'createdDate' },
        { label: 'Domain', key: 'sourceDomain' },
        { label: 'Word Count', key: 'wordCount' },
        { label: 'Collections', key: 'collections' },
        { label: 'Topics', key: 'topics' },
        { label: 'Keywords', key: 'keywords' }
      ]

      this.assets.forEach(asset => {
        if (!isEmpty(asset.properties)) {
          asset.properties
            .forEach(prop => {
              const [ name ] = prop.split('__')
              columns.push({ label: name, key: name })
            })
        }
      })

      return uniqBy(columns, col => col.key)
    },
    tableRows () {
      return this.assets.map(assetToRow)
    },
    propertySlotNames () {
      return this.tableColumns
        .filter(col => !includes(['title', 'topics', 'keywords'], col.key))
        .map(col => `cell--${col.key}`)
    },
    computedSelectedAssetIndexes: {
      get () {
        return this.selectedAssetIndexes
      },
      set (value) {
        this.$emit('selected-asset-indexes-changed', value)
      }
    }
  },

  methods: {
    onPageChange (newPage) {
      this.$emit('page-change', newPage)
    },
    detailRoute (rowIdx)  {
      const asset = this.assets[rowIdx]

      return {
        name: 'assetDetail',
        params: {
          brandName: this.$route.params.brandName,
          pageUrlId: asset.pageUrlId
        }
      }
    },
    truncate (value, length) {
      return value.length > length
        ? `${value.substring(0, length)}...`
        : value
    },
    assetIsSelected (idx) {
      return includes(this.computedSelectedAssetIndexes, idx)
    },
    onCheckboxClick (e, idx) {
      if (e.shiftKey) {
        this.onAssetShiftSelect(idx)
      } else {
        this.onAssetSelect(idx)
      }
    },
    onAssetSelect (idx) {
      if (this.assetIsSelected(idx)) {
        this.computedSelectedAssetIndexes = without(this.computedSelectedAssetIndexes, idx)
      } else {
        const newIndexes = this.computedSelectedAssetIndexes.slice()

        newIndexes.push(idx)
        newIndexes.sort((a, b) => a - b)
        this.computedSelectedAssetIndexes = newIndexes
      }
    },
    onAssetShiftSelect (idx) {
      const lastSelectedIndex = this.computedSelectedAssetIndexes
        .reduce((acc, val) => {
          if (val > acc && val < idx) {
            return val
          } else {
            return acc
          }
        }, 0)

      const newSelectedIndexes = range(lastSelectedIndex, idx + 1)

      const selectedIndexesCopy = this.computedSelectedAssetIndexes.slice()
      const mergedIndexes = selectedIndexesCopy.concat(newSelectedIndexes)
        .filter(idx_ => idx_ <= idx)

      this.computedSelectedAssetIndexes = uniq(mergedIndexes)
    }
  }
}
</script>

<style lang="scss" scoped>
.organizeAssetTable {
  padding-left: 20px;
  padding-bottom: 80px;
  width: 100%;

  &-title {
    width: 200px;
    display: flex;

    span {
      display: block;
      white-space: initial;
      cursor: pointer;
      margin-left: 7px;
    }
  }

  &-pagination {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 20px;
  }

  &-noAssets {
    width: 100%;
    height: 280px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 22px;
    font-weight: 500;

    &-logo {
      height: 200px;
      background: no-repeat center url('../../assets/images/no-results.svg');
      p {
        position: relative;
        top: 52px;
      }
    }
  }
}
</style>
